import { useId, useRef, useState, type FormEvent, type RefObject } from 'react';
import { t } from '../../../i18n';
import { useToast } from '../../../lib/toast';

export const MAX_TEXT = 2000;
// Ab hier zeigt das Eingabefeld, wie viele Zeichen noch gehen.
const HINT_FROM = 1800;

// Eingabe unten im Chat: 📎 (eine PDF oder ein Foto), Text, „Senden“. Enter sendet.
// Der Entwurf bleibt stehen, wenn neue Nachrichten ankommen; schlägt das Senden fehl, kommt er zurück.
export function ChatComposer({
  clipTitle,
  inputRef,
  onSendText,
  onSendFile,
}: {
  clipTitle: string;
  inputRef: RefObject<HTMLInputElement | null>;
  onSendText(text: string): Promise<unknown>;
  onSendFile(file: File): void;
}) {
  const [draft, setDraft] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);
  const toast = useToast();
  const hintId = useId();
  const near = draft.length >= HINT_FROM;

  async function submit(e: FormEvent) {
    e.preventDefault();
    const text = draft.trim();
    if (!text) return;
    setDraft('');
    inputRef.current?.focus();
    try {
      await onSendText(text);
    } catch {
      setDraft((d) => d || text);
      toast(t('Nachricht nicht gesendet, bitte erneut versuchen'));
    }
  }

  return (
    <>
      <form className="chat-in" onSubmit={submit}>
        <button type="button" className="clip" title={clipTitle} aria-label={t('Datei anhängen')} onClick={() => fileRef.current?.click()}>
          📎
        </button>
        <input
          ref={fileRef}
          type="file"
          accept="application/pdf,image/*"
          hidden
          onChange={(e) => {
            const f = e.target.files?.[0];
            e.target.value = '';
            if (f) onSendFile(f);
          }}
        />
        <input
          ref={inputRef}
          type="text"
          placeholder={t('Nachricht schreiben …')}
          aria-label={t('Nachricht')}
          autoComplete="off"
          maxLength={MAX_TEXT}
          value={draft}
          aria-describedby={near ? hintId : undefined}
          onChange={(e) => setDraft(e.target.value)}
        />
        <button className="send" type="submit">
          {t('Senden')}
        </button>
      </form>
      {near && (
        <div id={hintId} className="chat-limit">
          {t('{n} von {max} Zeichen', { n: draft.length, max: MAX_TEXT })}
        </div>
      )}
    </>
  );
}

// PDF oder Bild? Die Art steht nicht immer im Browser (z. B. HEIC), dann zählt die Endung.
export function isChatFile(f: File): boolean {
  return /^(application\/pdf|image\/)/.test(f.type) || /\.(pdf|jpe?g|png|gif|webp|heic|heif)$/i.test(f.name);
}
